import React from "react"
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts"
import "./styles.scss"
import { useMediaQuery } from "react-responsive"
import { isMobile } from "react-device-detect"

const COLORS = [
  "#FF4001",
  "#FFCC03",
  "#42A67C",
  "#0B7348"
]

const estimateTextWidth = (text, fontSize = 12) => {
  const averageCharWidth = fontSize * 0.6
  return text.length * averageCharWidth
}
const getTextWidth = (text, fontSize) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = `${fontSize}px Arial`;
  return context.measureText(text).width;
};

const breakText = (text, maxWidth, fontSize) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  let lineCount = 0;

  words.forEach((word) => {
    const testLine = currentLine + word + ' ';
    const width = getTextWidth(testLine, fontSize);

    if (width > maxWidth && lineCount < 1) {
      lines.push(currentLine.trim());
      currentLine = word + ' ';
      lineCount++;
    } else {
      currentLine += word + ' ';
    }
  });

  if (currentLine) {
    lines.push(currentLine.trim());
  }

  return lines.slice(0, 2);
};

const CustomizedXAxisTick = ({ x, y, payload, maxWidth }) => {
  const fontSize = 12;
  const lines = breakText(payload.value, maxWidth, fontSize);

  return (
    <g transform={`translate(${x},${y}) rotate(-20)`}> {/* Rotação de 90 graus para vertical */}
      {lines.map((line, index) => (
        <text
          key={index}
          x={0}
          y={index * 10} // Ajusta o espaçamento vertical entre as linhas
          dy={index === 0 ? 0 : 10} // Ajusta a distância vertical da segunda linha
          textAnchor="end" // Ajusta para melhor alinhamento
          className="ranking-label"
          fill="#666"
          fontSize={fontSize}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const CustomYAxisLabel = ({ x, y, payload }) => {
  const fontSize = 12;
  const limit = 340;

  const truncateText = (text) => {
    const words = text.split(" ");
    let truncated = "";
    let currentWidth = 0;

    for (let word of words) {
      const testLine = truncated ? `${truncated} ${word}` : word;
      const testWidth = estimateTextWidth(testLine, fontSize);

      if (testWidth > limit) {
        return truncated.trim() + "...";
      } else {
        truncated = testLine;
        currentWidth = testWidth;
      }
    }

    return truncated;
  };

  const truncatedText = truncateText(payload.value);

  return (
    <text
      x={x}
      y={y}
      fill="#000"
      fontSize={fontSize}
      textAnchor="end"
      className="ranking-label"
      style={{ whiteSpace: "nowrap" }}
    >
      <tspan x={x} dy={0}>
        {truncatedText}
      </tspan>
    </text>
  );
};

export const RankingChart = ({ orientation = "vertical", data }) => {
  const formattedData = Array.isArray(data) && data.length > 0 && data.map((item) => ({ ...item, total: +item.total }));

  const M = useMediaQuery({ query: "(min-width:1280px) and (max-width:1919px)" })

  const maxHeight = M ? 432 : 570
  const chartHeight = Math.min(maxHeight, data.length * 52);

  return (
    <div style={{ height: chartHeight, maxHeight: `${maxHeight}px`, overflowY: "auto" }}>
      <ResponsiveContainer width="100%" height={data.length * 52}>
        <BarChart
          data={formattedData}
          layout={orientation}
          margin={{ top: 20, right: 80, left: 20, bottom: 0 }}
          barCategoryGap={15}>
          <XAxis type="number" hide />
          <YAxis dataKey="formInfo" type="category" width={300} tick={<CustomYAxisLabel />} />
          <Bar dataKey="total" fill="#0B7348" barSize={30} radius={[4, 4, 4, 4]}>
            <LabelList dataKey="total" position="right" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const InteractionChart = ({ data }) => {

  const levelOfInteractionKeysMap = {
    "Produtor interagiu mas não aplicou o atendimento": "Interagiu mas não aplicou",
    "Produtor não interagiu ao atendimento": "Não interagiu",
    "Produtor aplicou de forma total": "Aplicou totalmente",
    "Produtor aplicou de forma parcial": "Aplicou parcialmente"
  }

  const changeValues = (arr, keysMap) => {
    return arr.map(obj => {
      if (keysMap[obj.title]) {
        return {
          ...obj,
          title: keysMap[obj.title],
          level: +obj.level
        };
      }
      return obj;
    });
  };

  const chartWidth = isMobile ? 350 : 550;
  // const chartWidth = 550;
  const numBars = data.length;
  const barWidth = 40;
  const maxWidth = chartWidth / numBars - 20;


  const formattedData = changeValues(data, levelOfInteractionKeysMap)

  return (
    <BarChart
      width={chartWidth}
      height={400}
      data={formattedData}
      barGap={16}
      margin={{ top: 20, right: 20, left: 0, bottom: 70 }}
    >
      <XAxis
        dataKey="title"
        tick={<CustomizedXAxisTick maxWidth={maxWidth} />}
        tickMargin={20}
        angle={50}
      />
      <Bar
        dataKey="level"
        barSize={barWidth}
        radius={[4, 4, 4, 4]}
      >
        {formattedData.map((_, index) => (
          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
        ))}
      </Bar>
    </BarChart>
  );
};
